import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CustomerT } from "../../types";
import {
  getCustomer,
  getInvoiceStats,
  getPaymentReminder,
} from "../../backend-services";
import { CustomerDetails } from "../../components/customers/view/customerDetail";
import { IInvoice, InvoiceStatsData } from "../../@types";
import PageLoader from "../../components/common/PageLoader";
import { GroupButton, SidebarDrawer } from "../../components/common";
import { CustomerDeletePrompt } from "../../components/customers/CustomerDelete";
import LeftNotificationPopup from "../../components/core/NotificationPopup/LeftNotificationPopup";
import ProcessSuccessful from "../../assets/icons/ProcessSuccessful";
import AnnotationComponent from "../../components/common/Annotation/AnnotationComponent";
import { Annotation } from "../../@types/annotation";
import useAnnotations from "../../hooks/annotation/useAnnotations";
import { SendReminderPrompt } from "../../components/Sales/invoice/SendReminderPrompt";
import { useLocation, useNavigate } from "react-router-dom";
import { AddSquareIcon } from "../../assets/icons";
import EditIcon from "../../assets/icons/EditIcon";
import NewCreate from "../../assets/icons/NewCreate";
import NewEdit from "../../assets/icons/NewEdit";
import DeleteIcon from "../../assets/icons/NewDelete";
import usePreviousLocation from "../../hooks/usePreviousLocation";
import { useTranslation } from "react-i18next";
import ActionModal from "../../components/common/ActionModal";
import UploadIcon from "../../assets/icons/svgicons/createInvoice.svg";
import { useUserStore } from "../../state";
import { BUSINESS_TYPE_RETAIL } from "../../constants";
import useRetailBusinessStatus from "../../hooks/invoice/useRetailBusinessStatus";
import generateActionButtons from "../../components/Sales/invoice/invoiceCreationModal";
import { trackEvent } from "../../utils/trackEvent";
import { showToast } from "../../utils/toastUtils";
import { useSubscription } from "../../context/SubscriptionProvider";

const initialState = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  address: "",
  city: "",
  state: "",
  businessName: "",
};

// Define the props for the ViewCustomers component
interface ViewCustomersProps {
  customerId: string; // The ID of the customer to view
  onClose: () => void; // Function to close the modal
}

export const ViewCustomers = () => {
  const { canUseModule, triggerSubscriptionModal } = useSubscription();
  const { customerId } = useParams();
  if (!customerId) throw new Error("Invoice Id is missing");
  const { t } = useTranslation("customer");
  const [customer, setCustomer] = useState<CustomerT>(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [DrawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [paymentReminder, setPaymentReminder] = useState(null);
  const [isReminderSent, setIsReminderSent] = React.useState(false);
  const [invoice, setInvoice] = useState<IInvoice>();
  const [annotations, setAnnotations] = React.useState<Annotation[]>([]);
  const annotationsData = useAnnotations(annotations, "Customer");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isRetailBusiness = useRetailBusinessStatus();
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerT | null>(
    null,
  );

  const handleOpenModal = (customer: CustomerT) => {
    setSelectedCustomer(customer); // Store the selected customer in state
    setIsModalOpen(true); // Open the modal
  };

  const handleCloseModal = () => {
    setSelectedCustomer(null); // Clear the selected customer data
    setIsModalOpen(false); // Close the modal
  };

  // ✅ Corrected openModal function
  const openModal = (selectedCustomer: CustomerT) => {
    setCustomer(selectedCustomer); // Store selected customer
    setIsSidebarOpen(true); // Open the sidebar/modal
  };

  const [modalData, setModalData] = useState({
    customerId: "",
    customerLastName: "",
    customerFirstName: "",
    customerPhone: "",
    customerBusinessName: "",
  });

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([getCustomerHandler()]);
        trackEvent("customer_detail_view"); // Event Tracking on customer_detail_view
      } catch (error) {
        trackEvent("customer_detail_error"); // Event Tracking on customer_detail_error
        console.error(error);
        showToast("Failed to load customer data.", "error");
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [customerId]);

  const getCustomerHandler = async () => {
    const data = await getCustomer(customerId);
    setCustomer(data);
    if (data?.metadata) setAnnotations(data?.metadata?.annotations);
  };

  const toggleDrawer = () => {
    setDrawerOpen((prevState) => !prevState);
  };

  const handleCustomerDeleteSuccess = () => {
    setShowSuccessModal(true);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const PaymentReminder = async () => {
    try {
      if (customerId) {
        const reminder = await getPaymentReminder(customerId);
        setPaymentReminder(reminder);
        setIsReminderSent(true);
      }
    } catch (error) {
      showToast("Failed to send payment reminder.", "error");
    }
  };
  const navigate = useNavigate();

  const handleCreateInvoice = () => {
    const customerId = customer?.id ?? "";
    const customerFirstName = `${customer?.firstName}`;
    const customerLastName = `${customer?.lastName}`;
    const customerPhone = customer?.phone ?? "";
    const customerBusinessName = customer?.businessName ?? "";

    setModalData({
      customerId,
      customerFirstName,
      customerLastName,
      customerPhone,
      customerBusinessName,
    });

    setIsModalOpen(true);
  };
  const handleInventoryInvoiceClick = (modalData: {
    customerId: any;
    customerFirstName: any;
    customerLastName: any;
    customerPhone: any;
    customerBusinessName: any;
  }) => {
    const {
      customerId,
      customerFirstName,
      customerLastName,
      customerPhone,
      customerBusinessName,
    } = modalData;
    navigate(
      `/invoices/create?customerId=${encodeURIComponent(
        customerId,
      )}&customerFirstName=${encodeURIComponent(
        customerFirstName,
      )}&customerLastName=${encodeURIComponent(
        customerLastName,
      )}&customerPhone=${encodeURIComponent(
        customerPhone,
      )}&customerBusinessName=${encodeURIComponent(
        customerBusinessName,
      )}&invoiceType=inventory`,
    );
  };

  const handleFreeformInvoiceClick = (modalData: {
    customerId: any;
    customerLastName: any;
    customerFirstName: any;
    customerPhone: any;
    customerBusinessName: any;
  }) => {
    const {
      customerId,
      customerLastName,
      customerFirstName,
      customerPhone,
      customerBusinessName,
    } = modalData;
    navigate(
      `/invoices/createFreeform?customerId=${encodeURIComponent(
        customerId,
      )}&customerLastName=${encodeURIComponent(
        customerLastName,
      )}&customerFirstName=${encodeURIComponent(
        customerFirstName,
      )}&customerPhone=${encodeURIComponent(
        customerPhone,
      )}&customerBusinessName=${encodeURIComponent(
        customerBusinessName,
      )}&invoiceType=freeform`,
    );
  };
  const actionButtons = generateActionButtons(
    isRetailBusiness,
    () => {
      const {
        customerId,
        customerLastName,
        customerFirstName,
        customerPhone,
        customerBusinessName,
      } = modalData;
      handleInventoryInvoiceClick({
        customerId,
        customerLastName,
        customerFirstName,
        customerPhone,
        customerBusinessName,
      });
    },
    () => {
      const {
        customerId,
        customerLastName,
        customerFirstName,
        customerPhone,
        customerBusinessName,
      } = modalData;
      handleFreeformInvoiceClick({
        customerId,
        customerLastName,
        customerFirstName,
        customerPhone,
        customerBusinessName,
      });
    },
  );

  return (
    <>
      {isLoading ? (
        <PageLoader />
      ) : (
        <div className="">
          <div className="flex items-center justify-between ">
            <div className="flex lg:items-center gap-[1em] items-center">
              <a className="cursor-pointer" onClick={() => navigate(-1)}>
                <svg
                  className=""
                  width="10"
                  height="16"
                  viewBox="0 0 10 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8.5 1L1.5 8L8.5 15"
                    stroke="#33363F"
                    strokeWidth="2"
                  />
                </svg>
              </a>
              <h1 className="flex items-center text-[18px] md:text-[20px] md:leading-7 font-semibold">
                Customer Details
              </h1>
            </div>
            <div className="md:flex md:gap-4 md:items-center">
              <div className="hidden md:block ">
                <GroupButton
                  buttons={[
                    {
                      icon: <NewCreate className="mr-1" />,
                      text: t("customer.createInvoice"),
                      onClick: canUseModule("invoices").allowed
                        ? handleCreateInvoice
                        : () => triggerSubscriptionModal("invoices"),
                      fontSize: "font-semibold text-[16px] gap-1 text-gray-700",
                    },
                    {
                      icon: <NewEdit className="mr-1" />,
                      text: t("customer.edit"),
                      onClick: toggleDrawer,
                      fontSize:
                        "font-semibold text-[16px] gap-1   text-gray-700",
                    },
                    {
                      icon: <DeleteIcon />,
                      text: "Delete",
                      onClick: () => setShowPrompt(true),
                      textColorClass: "text-[#FB0303]",
                      fontSize:
                        "font-semibold text-[16px] text-[#FB0303] gap-1",
                    },
                  ]}
                />
              </div>
              <div className="hidden md:flex">
                <AnnotationComponent
                  type="customer"
                  annotationData={annotationsData}
                />
              </div>
            </div>
          </div>
          <div>
            <CustomerDetails
              handleCreateInvoice={handleCreateInvoice}
              customer={customer}
              DrawerOpen={DrawerOpen}
              showIsLoading={() => {}}
              hideIsLoading={() => {}}
              showPrompt={showPrompt}
              toggleDrawer={toggleDrawer}
              setShowPrompt={setShowPrompt}
              setIsSidebarOpen={setIsSidebarOpen}
            />
          </div>
        </div>
      )}
      <div ref={containerRef}>
        <ActionModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title="Select an invoice creation option"
          icon={UploadIcon}
          actionButtons={actionButtons}
        />
      </div>

      {showPrompt && (
        <CustomerDeletePrompt
          showPrompt={showPrompt}
          show={() => setShowPrompt(false)}
          customerId={customerId}
          onClose={() => setShowPrompt(false)}
          onDeleteSuccess={handleCustomerDeleteSuccess}
        />
      )}
      {isSidebarOpen && (
        <SidebarDrawer title={"Send Reminder"} show={true} position={"left"}>
          <SendReminderPrompt
            onSend={PaymentReminder}
            onClose={() => setIsSidebarOpen(false)}
            isSidebarOpen={isSidebarOpen}
            invoice={invoice}
            customerId={customerId}
            //@ts-ignore
            customerEmail={customer.email}
          />
        </SidebarDrawer>
      )}
      {isReminderSent && (
        <SidebarDrawer title={""} show={true} position={"left"}>
          <LeftNotificationPopup
            title={"You sent a payment reminder"}
            icon={<ProcessSuccessful />}
          />
        </SidebarDrawer>
      )}
    </>
  );
};
